import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import type { StatementFee, StatementJob, StatementLeadInput } from '@/utils/partnerStatement';

/**
 * Referral partners: external companies that send leads and are owed a fee.
 *
 *   referral_partners  one per partner; owns one lead_sources row, so
 *                      attribution is leads.lead_source_id (or the campaign's)
 *   referral_fees      one per lead, derived by the DB (never typed by hand):
 *                      accrued -> payable -> paid, or void
 *   referral_payouts   a payment covering a set of payable fees
 *
 * Admin-only end to end (RLS). Migration: 20261005100000_referral_partners.sql
 */

export type FeeBasis = 'per_lead' | 'per_job' | 'percent_revenue' | 'percent_gross_profit' | 'percent_profit';
export type PayWhen = 'job_completed' | 'job_paid';
export type FeeStatus = 'accrued' | 'payable' | 'paid' | 'void';

export const FEE_BASIS_LABEL: Record<FeeBasis, string> = {
  per_lead: 'Flat fee per lead',
  per_job: 'Flat fee per completed job',
  percent_revenue: '% of job revenue (ex tax)',
  percent_gross_profit: '% of job profit before tech commission',
  percent_profit: '% of company profit (after tech commission)',
};

export const PAY_WHEN_LABEL: Record<PayWhen, string> = {
  job_completed: 'when the job is completed',
  job_paid: 'once the job is paid',
};

export interface ReferralPartner {
  id: string;
  company_id: string;
  lead_source_id: string | null;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
  fee_basis: FeeBasis;
  fee_amount: number;
  fee_percent: number;
  pay_when: PayWhen;
  fee_start_date: string;
  is_active: boolean;
  created_at: string;
  lead_sources: { id: string; name: string; is_active: boolean } | null;
}

export interface ReferralPartnerInput {
  name: string;
  lead_source_id: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
  fee_basis: FeeBasis;
  fee_amount: number;
  fee_percent: number;
  pay_when: PayWhen;
  fee_start_date: string;
  is_active: boolean;
}

export interface ReferralPartnerTotals {
  partner_id: string;
  accrued: number;
  payable: number;
  paid: number;
  fee_count: number;
  last_lead_at: string | null;
}

export interface ReferralFeeRow {
  id: string;
  partner_id: string;
  lead_id: string;
  job_id: string | null;
  fee_basis: FeeBasis;
  base_amount: number | null;
  rate: number;
  amount: number;
  status: FeeStatus;
  payable_at: string | null;
  paid_at: string | null;
  payout_id: string | null;
  void_reason: string | null;
  notes: string | null;
  created_at: string;
  leads: { lead_number: string; customer_name: string; created_at: string; status: string } | null;
  jobs: { job_number: string; status: string; payment_status: string; invoice_total: number | null } | null;
}

export interface ReferralPayoutRow {
  id: string;
  partner_id: string;
  amount: number;
  fee_count: number;
  paid_on: string;
  method: string | null;
  reference: string | null;
  notes: string | null;
  created_at: string;
}

const PARTNER_COLUMNS =
  'id, company_id, lead_source_id, name, contact_name, contact_email, contact_phone, notes, fee_basis, fee_amount, fee_percent, pay_when, fee_start_date, is_active, created_at, lead_sources (id, name, is_active)';

const FEE_COLUMNS =
  'id, partner_id, lead_id, job_id, fee_basis, base_amount, rate, amount, status, payable_at, paid_at, payout_id, void_reason, notes, created_at, leads (lead_number, customer_name, created_at, status), jobs (job_number, status, payment_status, invoice_total)';

/** Describe a void reason the engine or the office wrote. */
export function describeVoidReason(reason: string | null): string {
  if (!reason) return '';
  if (reason === 'job_cancelled') return 'Job cancelled';
  if (reason === 'no_partner') return 'Lead no longer from this partner';
  if (reason === 'before_start_date') return 'Before the fee start date';
  if (reason.startsWith('manual')) {
    const note = reason.replace(/^manual:?\s*/, '');
    return note ? `Voided by office: ${note}` : 'Voided by office';
  }
  return reason;
}

/** One line for the partner table: "$25.00 per lead", "10% of job revenue, once paid". */
export function describeFeeRule(p: Pick<ReferralPartner, 'fee_basis' | 'fee_amount' | 'fee_percent' | 'pay_when'>, formatMoney: (n: number) => string): string {
  switch (p.fee_basis) {
    case 'per_lead':
      return `${formatMoney(p.fee_amount)} per lead`;
    case 'per_job':
      return `${formatMoney(p.fee_amount)} per job, ${PAY_WHEN_LABEL[p.pay_when]}`;
    case 'percent_revenue':
      return `${p.fee_percent}% of job revenue, ${PAY_WHEN_LABEL[p.pay_when]}`;
    case 'percent_gross_profit':
      return `${p.fee_percent}% of profit before tech, ${PAY_WHEN_LABEL[p.pay_when]}`;
    case 'percent_profit':
      return `${p.fee_percent}% of company profit, ${PAY_WHEN_LABEL[p.pay_when]}`;
  }
}

export function useReferralPartners() {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: ['referral-partners', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<ReferralPartner[]> => {
      const { data, error } = await supabase
        .from('referral_partners')
        .select(PARTNER_COLUMNS)
        .eq('company_id', tenantId!)
        .order('is_active', { ascending: false })
        .order('name');
      if (error) throw error;
      return (data ?? []) as unknown as ReferralPartner[];
    },
  });
}

export function useReferralPartnerTotals() {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: ['referral-partner-totals', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<Record<string, ReferralPartnerTotals>> => {
      const { data, error } = await supabase.rpc('referral_partner_totals', { _company_id: tenantId! });
      if (error) throw error;
      const out: Record<string, ReferralPartnerTotals> = {};
      for (const row of (data ?? []) as ReferralPartnerTotals[]) out[row.partner_id] = row;
      return out;
    },
  });
}

export function useSaveReferralPartner() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: ReferralPartnerInput }) => {
      if (!tenantId) throw new Error('No company selected');
      if (id) {
        const { error } = await supabase.from('referral_partners').update(input).eq('id', id);
        if (error) throw error;
        return id;
      }
      const { data, error } = await supabase
        .from('referral_partners')
        .insert({ ...input, company_id: tenantId })
        .select('id')
        .single();
      if (error) throw error;
      return data.id as string;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referral-partners', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['referral-partner-totals', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['referral-fees'] });
      // the partner may have created a lead source
      queryClient.invalidateQueries({ queryKey: ['lead-sources'] });
    },
  });
}

export function useReferralFees(partnerId: string | null, statuses: FeeStatus[]) {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: ['referral-fees', tenantId, partnerId, statuses.slice().sort().join(',')],
    enabled: !!tenantId && !!partnerId && statuses.length > 0,
    queryFn: async (): Promise<ReferralFeeRow[]> => {
      const { data, error } = await supabase
        .from('referral_fees')
        .select(FEE_COLUMNS)
        .eq('company_id', tenantId!)
        .eq('partner_id', partnerId!)
        .in('status', statuses)
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as unknown as ReferralFeeRow[];
    },
  });
}

/**
 * Everything a partner statement needs for one period: the partner's leads
 * (by its source, or a campaign of its source, as the fee engine attributes
 * them), each lead's latest job and its fee row. `startIso` inclusive,
 * `endIso` exclusive.
 */
export function usePartnerStatementLeads(partner: ReferralPartner | null, startIso: string, endIso: string) {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: ['referral-statement', tenantId, partner?.id, startIso, endIso],
    enabled: !!tenantId && !!partner?.lead_source_id,
    queryFn: async (): Promise<StatementLeadInput[]> => {
      const src = partner!.lead_source_id!;
      const { data: camps, error: cErr } = await supabase
        .from('campaigns').select('id').eq('company_id', tenantId!).eq('lead_source_id', src);
      if (cErr) throw cErr;
      const campaignIds = (camps ?? []).map((c) => c.id);
      const attribution = campaignIds.length ? `lead_source_id.eq.${src},campaign_id.in.(${campaignIds.join(',')})` : `lead_source_id.eq.${src}`;

      const { data: leads, error: lErr } = await supabase
        .from('leads')
        .select('id, lead_number, created_at, customer_name, customer_suburb, status')
        .eq('company_id', tenantId!)
        .or(attribution)
        .gte('created_at', startIso)
        .lt('created_at', endIso)
        .order('created_at')
        .limit(2000);
      if (lErr) throw lErr;
      const rows = leads ?? [];
      if (!rows.length) return [];

      const jobs = new Map<string, StatementJob & { lead_id: string; created_at: string }>();
      const fees = new Map<string, StatementFee>();
      for (let i = 0; i < rows.length; i += 150) {
        const ids = rows.slice(i, i + 150).map((l) => l.id);
        const [{ data: js, error: jErr }, { data: fs, error: fErr }] = await Promise.all([
          supabase.from('jobs')
            .select('id, lead_id, created_at, job_number, status, scheduled_date, job_date, invoice_total, revenue_before_gst, payment_status')
            .eq('company_id', tenantId!).in('lead_id', ids),
          supabase.from('referral_fees')
            .select('lead_id, amount, status, base_amount, rate, paid_at, void_reason')
            .eq('company_id', tenantId!).eq('partner_id', partner!.id).in('lead_id', ids),
        ]);
        if (jErr) throw jErr;
        if (fErr) throw fErr;
        // The engine prices the lead's most recent job; do the same.
        for (const j of js ?? []) {
          const prev = jobs.get(j.lead_id);
          if (!prev || j.created_at > prev.created_at) jobs.set(j.lead_id, j as StatementJob & { lead_id: string; created_at: string });
        }
        for (const f of fs ?? []) fees.set(f.lead_id, f as unknown as StatementFee);
      }
      return rows.map((l) => ({ ...l, job: jobs.get(l.id) ?? null, fee: fees.get(l.id) ?? null }));
    },
  });
}

export function useReferralPayouts(partnerId: string | null) {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: ['referral-payouts', tenantId, partnerId],
    enabled: !!tenantId && !!partnerId,
    queryFn: async (): Promise<ReferralPayoutRow[]> => {
      const { data, error } = await supabase
        .from('referral_payouts')
        .select('id, partner_id, amount, fee_count, paid_on, method, reference, notes, created_at')
        .eq('company_id', tenantId!)
        .eq('partner_id', partnerId!)
        .order('paid_on', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return (data ?? []) as ReferralPayoutRow[];
    },
  });
}

export interface RecordPayoutInput {
  partnerId: string;
  feeIds: string[];
  paidOn: string; // YYYY-MM-DD
  method: string | null;
  reference: string | null;
  notes: string | null;
}

function invalidateLedger(queryClient: ReturnType<typeof useQueryClient>, tenantId: string | null | undefined) {
  queryClient.invalidateQueries({ queryKey: ['referral-fees'] });
  queryClient.invalidateQueries({ queryKey: ['referral-payouts'] });
  queryClient.invalidateQueries({ queryKey: ['referral-partner-totals', tenantId] });
}

export function useRecordReferralPayout() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  return useMutation({
    mutationFn: async (input: RecordPayoutInput) => {
      const { data, error } = await supabase.rpc('record_referral_payout', {
        _partner_id: input.partnerId,
        _fee_ids: input.feeIds,
        _paid_on: input.paidOn,
        _method: input.method ?? undefined,
        _reference: input.reference ?? undefined,
        _notes: input.notes ?? undefined,
      });
      if (error) throw error;
      return data as { payout_id: string; amount: number; fee_count: number };
    },
    onSuccess: () => invalidateLedger(queryClient, tenantId),
  });
}

export function useVoidReferralFee() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  return useMutation({
    mutationFn: async ({ feeId, reason }: { feeId: string; reason: string | null }) => {
      const { error } = await supabase.rpc('void_referral_fee', { _fee_id: feeId, _reason: reason ?? undefined });
      if (error) throw error;
    },
    onSuccess: () => invalidateLedger(queryClient, tenantId),
  });
}

export function useRestoreReferralFee() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  return useMutation({
    mutationFn: async (feeId: string) => {
      const { error } = await supabase.rpc('restore_referral_fee', { _fee_id: feeId });
      if (error) throw error;
    },
    onSuccess: () => invalidateLedger(queryClient, tenantId),
  });
}
